import { useMemo, useState, useCallback } from 'react';

type ValueType = boolean | number | string | null;
type ValueTypeString = 'boolean' | 'number' | 'string';
interface Options {
  enable?: boolean;
  prefix?: string;
}

// Same as useState, except it reads from query params on page load, and writes to them on change
// Query param behavior can be disabled so this can conditionally be identical to useState
// A prefix can be provided to avoid conflicts with other uses of this hook in the URL
export function useQueryParamState<T extends ValueType>(
  name: string,
  type: ValueTypeString,
  defaultValue: T | null = null,
  { enable = true, prefix = '' }: Options = { enable: true, prefix: '' }
) {
  const initialValue = useMemo(() => readValue(name, prefix, type, defaultValue), []) as T;
  const [value, setValue] = useState<T>(initialValue);

  const setValueAndSync = useCallback((value) => {
    setValue(value);

    if (enable) {
      writeValue(name, prefix, value, defaultValue);
    }
  }, [enable]);

  return [value, setValueAndSync];
}

// Like useQueryParamState, but for an object of key/value pairs, e.g:
//   const [object, setObject] = useQueryParamsState({ a: 'string', b: 'number' }, { a: 'default', b: 0 })
export function useQueryParamsState<T extends Record<keyof T, ValueType>>(
  config: Record<keyof T, ValueTypeString>,
  defaultValue: T = {} as T,
  { enable = true, prefix = '' }: Options = { enable: true, prefix: '' }
): [T, (newValues: T) => void] {
  const initialValue = useMemo(() => {
    const initialValue: Partial<Record<keyof T, ValueType>> = {};
    for (const name in config) {
      initialValue[name] = readValue(name, prefix, config[name], defaultValue[name]);
    }
    return initialValue as T;
  }, [config, defaultValue]);

  const [value, _setValue] = useState<T>(initialValue);

  const setValue = useCallback((value) => {
    _setValue(value);

    if (enable) {
      for (const name in value) {
        writeValue(name, prefix, value[name], defaultValue[name]);
      }
    }
  }, [enable]);

  return [value, setValue];
}

function getKey(name: string, prefix: string): string {
  return prefix ? `${prefix}_${name}` : name;
}

function readValue(name: string, prefix: string, type: ValueTypeString, defaultValue: ValueType): ValueType {
  const key = getKey(name, prefix);
  const params = new URLSearchParams(window.location.search);

  if (params.has(key)) {
    return parseValue(params.get(key), type);
  } else {
    return defaultValue;
  }
}

function writeValue(name: string, prefix: string, value: ValueType, defaultValue: ValueType) {
  const key = getKey(name, prefix);
  const url = new URL(window.location.href);

  if (value == null || value === defaultValue) {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, serializeValue(value));
  }

  window.history.replaceState({}, '', url.toString());
}

function parseValue(value: string | null, type: ValueTypeString): ValueType {
  if (!value) {
    return null;
  }

  switch (type) {
  case 'boolean':
    return value === 'true';
  case 'number':
    return Number(value);
  default:
    return value;
  }
}

function serializeValue(value?: ValueType) {
  if (value == null) {
    return '';
  } else {
    return value.toString();
  }
}
